import React, { useState, useEffect } from 'react';

const SCHEMA_PROMPT = `You are helping me build a mobile presentation slide. I need you to generate or modify a JSON array of slide elements.

CANVAS: 360px wide. Elements are absolutely positioned. The slide can scroll vertically.

ELEMENT SCHEMA:
{
  "id": "unique_string",          // keep existing IDs, generate new ones for new elements
  "type": "text" | "image",
  "layout": {
    "x": number,                  // left offset in px
    "y": number,                  // top offset in px
    "width": number,              // width in px
    "height": number              // height in px
  },
  "zIndex": number,               // stacking order, higher = on top
  "style": {
    "backgroundColor": "string",  // hex / rgba, optional
    "color": "string",            // text color hex, optional
    "fontSize": number,           // px, optional
    "fontWeight": "bold"|"normal",
    "textAlign": "left"|"center"|"right",
    "borderRadius": number,       // px, optional
    "borderWidth": number,        // px, optional
    "borderColor": "string",      // hex, optional
    "boxShadow": "string",        // CSS box-shadow, optional
    "objectFit": "contain"|"cover" // images only, optional
  },
  "content": {
    // type "text"  → { "text": "your text" }
    // type "image" → { "src": "url", "alt": "description" }
  }
}

CURRENT SLIDE JSON:
`;

function buildPrompt(elements, topic) {
  const topicLine = topic.trim()
    ? `TOPIC: ${topic.trim()}`
    : `TOPIC: [fill in your topic here]`;
  return (
    SCHEMA_PROMPT +
    JSON.stringify(elements, null, 2) +
    `

${topicLine}

Build a complete, well-designed slide for the topic above. The canvas is currently empty — fill it with appropriate text and/or image elements. Reply with ONLY the JSON array. No explanation.`
  );
}

export function JsonEditor({ elements, onElementsChange }) {
  const [jsonString, setJsonString] = useState('');
  const [error, setError] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [topic, setTopic] = useState('');

  useEffect(() => {
    try {
      setJsonString(JSON.stringify(elements, null, 2));
      setError(null);
    } catch (e) {
      setError('Error converting elements to JSON.');
    }
  }, [elements]);

  const handleChange = (e) => setJsonString(e.target.value);

  const handleApply = () => {
    try {
      const parsed = JSON.parse(jsonString);
      if (!Array.isArray(parsed)) throw new Error('JSON must be an array of elements.');
      onElementsChange(parsed);
      setError(null);
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
    }
  };

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(buildPrompt(elements, topic)).then(() => {
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <h3 style={styles.header}>JSON Editor</h3>
        <button style={styles.aiToggle} onClick={() => setShowPrompt((v) => !v)}>
          {showPrompt ? 'Hide AI Prompt' : '✨ Get AI Prompt'}
        </button>
      </div>

      {showPrompt && (
        <div style={styles.promptBox}>
          <p style={styles.promptHint}>
            Enter your topic, then copy the prompt and paste it into any AI (ChatGPT, Claude, etc.). Paste the returned JSON below.
          </p>
          <input
            style={styles.topicInput}
            type="text"
            placeholder="e.g. Introduction to Machine Learning"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
          />
          <pre style={styles.promptPreview}>{buildPrompt(elements, topic)}</pre>
          <button style={styles.copyPromptBtn} onClick={handleCopyPrompt}>
            {promptCopied ? '✓ Copied!' : 'Copy Prompt'}
          </button>
        </div>
      )}

      <textarea
        style={styles.textarea}
        value={jsonString}
        onChange={handleChange}
        spellCheck={false}
      />
      {error && <p style={styles.error}>{error}</p>}
      <button style={styles.applyBtn} onClick={handleApply}>
        Apply JSON Changes
      </button>
    </div>
  );
}

const styles = {
  container: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    gap: '8px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '2px',
  },
  header: {
    fontSize: '1rem',
    margin: 0,
    color: '#333',
  },
  aiToggle: {
    background: '#f0edff',
    color: '#3B00FF',
    border: '1px solid #c4b8ff',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  promptBox: {
    background: '#fafafa',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  promptHint: {
    margin: 0,
    fontSize: '12px',
    color: '#555',
    lineHeight: '1.5',
  },
  topicInput: {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #c4b8ff',
    borderRadius: '6px',
    fontSize: '13px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  promptPreview: {
    margin: 0,
    fontSize: '11px',
    color: '#444',
    background: '#f0f0f0',
    borderRadius: '6px',
    padding: '10px',
    maxHeight: '160px',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'monospace',
  },
  copyPromptBtn: {
    alignSelf: 'flex-start',
    background: '#3B00FF',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  textarea: {
    flexGrow: 1,
    width: '100%',
    minHeight: '180px',
    padding: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  applyBtn: {
    backgroundColor: '#3B00FF',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  error: {
    color: 'red',
    fontSize: '0.8rem',
    margin: 0,
  },
};
